import React from 'react'

import getConfig from 'next/config'
import { ErrorProps } from 'next/error'

import { Store as StoreSd } from '@grandvisionhq/analytics'
import {
  CmsHeadTags,
  useBloomreachPageRoutes,
  useBloomreachSdpContent,
} from '@grandvisionhq/bloomreach'
import { Container } from '@grandvisionhq/elements'
import { NextPage, withErrorPage } from '@grandvisionhq/next'
import { useFeatureToggles, useIntl } from '@grandvisionhq/state'
import { DetailsPage, StoreDetailsRoot, Types, Util } from '@grandvisionhq/stores-v2'
import { compose, slugify } from '@grandvisionhq/utils'

import { withCms, withLayout } from '~/cms'
import { defaultErrorProps } from '~/components/error-props'
import { INTL_DEFAULTS } from '~/configuration'

const { publicRuntimeConfig } = getConfig()
const selfUrl = publicRuntimeConfig?.selfUrl || ''
const siteName = publicRuntimeConfig?.site.name || ''
const googleMapsApiKey = publicRuntimeConfig?.googleMaps.apiKey || ''

type InitialProps = {
  storeData?: Types.Store | null
}

type Props = InitialProps

const StoreDetailPage: NextPage<Props, InitialProps | ErrorProps> = ({ storeData }) => {
  const { getLabel, getFormattedLabel } = useIntl()
  const features = useFeatureToggles()
  const { routes } = useBloomreachPageRoutes()
  const { storeFallbackImage, paymentIcons, socialMediaIcons } = useBloomreachSdpContent()

  const pageTitle = storeData
    ? getFormattedLabel('stores.detail.page.title', {
        labelData: { town: storeData?.town, street: storeData?.streetName },
      })
    : ''
  const pageUrl = storeData
    ? `${selfUrl}${routes.stores}/${slugify(storeData.town)}/${storeData.slug}`
    : ''
  const breadcrumbs = storeData
    ? Util.createBreadcrumbs(storeData, getLabel('stores.form.findStore.title'), routes.stores)
    : []

  const urls = React.useMemo(
    () => ({
      bookAppointment: routes['book-appointment-form'],
      fallbackImage: storeFallbackImage,
    }),
    [routes, storeFallbackImage]
  )

  const logos = React.useMemo(
    () => ({
      paymentMethods: paymentIcons,
      socialMedia: socialMediaIcons,
    }),
    [paymentIcons, socialMediaIcons]
  )

  if (!storeData) {
    // tslint:disable-next-line:no-console
    console.error(
      `Tried to render a store detail page but got invalid store data: ${String(storeData)}`
    )
    return null
  }

  return (
    <>
      <CmsHeadTags title={pageTitle} description={storeData.metaDescription} ogUrl={pageUrl} />
      <Container className="stores-v2-layout">
        <StoreDetailsRoot
          store={storeData}
          hidePhone={!features.isStoreDetailsPhoneEnabled}
          hideEmail={!features.isStoreDetailsEmailEnabled}
          opco={siteName}
          breadcrumbs={breadcrumbs}
          googleMapsApiKey={googleMapsApiKey}
          urls={urls}
          logos={logos}
        />
      </Container>
      <StoreSd
        url={`${selfUrl}${routes.stores}`}
        store={{
          ...storeData,
          storeName: storeData.name,
          storeEmailAddress: storeData.emailAddress,
          Phone1: storeData.phone,
        }}
      />
    </>
  )
}

StoreDetailPage.getInitialProps = async (ctx) => {
  /*
   * TODO: Fix types: apolloClient is provided via the context, which is
   * missing the apolloClient property description at the moment.
   */
  const { asPath = '', apolloClient, req } = ctx as any
  const cleanPath = asPath.split('?')[0]
  const slug = [...cleanPath.split('/')].pop()
  const locale = req?.locale?.toString() || INTL_DEFAULTS.locale
  if (!slug) {
    throw new Error(`Can't extract slug from ${asPath}: '${String(slug)}'`)
  }

  try {
    const code = parseInt(slug, 10)
    return await DetailsPage.getPageData(apolloClient, isNaN(code) ? slug : code, locale)
  } catch (err) {
    // Non OK stores service api response, return props to show the error page
    // tslint:disable-next-line:no-console
    console.error(`Failed to fetch Store with slug: ${String(slug)}.`, err)
    return {
      ...defaultErrorProps[locale],
      statusCode: 500,
    }
  }
}

export default compose<Props, Props>(withErrorPage, withCms(), withLayout())(StoreDetailPage)
